import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { marked } from 'marked';
import { Observable } from 'rxjs';
import { SharedModule } from '@shared/shared.module';
import { VaultDateTimePipe } from '@shared/pipes/vault-date-time.pipe';
import { Permissions } from '@app/core/guard/Permissions';
import { PermissionsService } from '@services/helpers/permissions.service';
import { ToastHelperService } from '@app/core/services/helpers/toast-helper.service';
import { AccountingDeParaGovernanceService } from '@app/core/services/http/accounting-data-standardization/accounting-de-para-governance.service';
import {
  StandardAccountPromotionProposal,
  StandardAccountPromotionStatus,
} from '@interfaces/accounting-data-standardization/responses/standard-account-promotion.response';
import { describeBackendError } from '@src/app/features/dashboard/accounting-data-standardization/utils/backend-error.util';

const STATUS_LABELS: Record<StandardAccountPromotionStatus, string> = {
  Candidate: 'Candidata',
  Drafted: 'Rascunho pronto',
  Accepted: 'Aceita',
  Rejected: 'Recusada',
};

const PERCENT_FORMAT = new Intl.NumberFormat('pt-BR', {
  style: 'percent',
  maximumFractionDigits: 1,
});

/** Limite do motivo da decisão. */
const DECISION_REASON_MAX_LENGTH = 1000;

/**
 * Aba "Propostas de conta" da listagem da Padronização, só ADMIN (PBI 3423, DPA-16). Lista as subcontas que se repetem
 * entre empresas (ou pesam no grupo) e viraram candidatas a conta oficial do plano padrão, com o rascunho do Cog em
 * markdown. Aceitar ou recusar exige motivo. Aceitar cria a conta oficial sob a mesma conta padrão e reclassifica as
 * linhas das empresas (PBI 3423); o que ficar pendente a rotina semanal retoma, e quem tem a permissão de reclassificar
 * pode pedir de novo na hora.
 *
 * O markdown é convertido com `marked` e entra por `[innerHTML]`, que passa pelo sanitizador do Angular (sem
 * `bypassSecurityTrust`): o rascunho é texto gerado por IA.
 */
@Component({
  selector: 'app-standard-account-promotions-panel',
  standalone: true,
  imports: [FormsModule, SharedModule, VaultDateTimePipe],
  templateUrl: './standard-account-promotions-panel.component.html',
  styleUrl: './standard-account-promotions-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StandardAccountPromotionsPanelComponent {
  private readonly governance = inject(AccountingDeParaGovernanceService);
  private readonly toast = inject(ToastHelperService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly reasonMaxLength = DECISION_REASON_MAX_LENGTH;

  /** Aceitar/Recusar só com a permissão própria de decidir (a de listar abre a aba). */
  protected readonly canDecide = toSignal(
    inject(PermissionsService).hasPermission(Permissions.AccountingStandardAccountPromotion.DECIDE),
    { initialValue: false }
  );

  /** "Reclassificar de novo" só com a permissão própria (PBI 3423). */
  protected readonly canReclassify = toSignal(
    inject(PermissionsService).hasPermission(
      Permissions.AccountingStandardAccountPromotion.RECLASSIFY
    ),
    { initialValue: false }
  );

  protected readonly isReclassifying = signal(false);

  protected readonly proposals = signal<StandardAccountPromotionProposal[]>([]);
  protected readonly isLoading = signal(true);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly selectedId = signal<string | null>(null);
  protected readonly reason = signal('');
  protected readonly isDeciding = signal(false);

  protected readonly selected = computed(
    () => this.proposals().find(item => item.id === this.selectedId()) ?? null
  );

  /** Rascunho da proposta aberta, já em HTML (sanitizado pelo Angular no `[innerHTML]`). */
  protected readonly draftHtml = computed(() => {
    const markdown = this.selected()?.draftMarkdown?.trim();
    if (!markdown) return null;
    try {
      return marked.parse(markdown, { async: false, gfm: true, breaks: true }) as string;
    } catch {
      return null;
    }
  });

  protected readonly canSubmitDecision = computed(() => {
    const proposal = this.selected();
    const reason = this.reason().trim();
    return (
      !!proposal &&
      this.canDecide() &&
      !this.isDeciding() &&
      proposal.status !== 'Accepted' &&
      proposal.status !== 'Rejected' &&
      reason.length > 0 &&
      reason.length <= DECISION_REASON_MAX_LENGTH
    );
  });

  constructor() {
    this.load();
  }

  load(): void {
    this.isLoading.set(true);
    this.errorMessage.set(null);
    this.governance
      .getPromotionProposals()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: list => {
          this.proposals.set(list ?? []);
          this.isLoading.set(false);
          if (!this.selected()) this.selectedId.set(list?.[0]?.id ?? null);
        },
        error: (err: unknown) => {
          this.isLoading.set(false);
          this.errorMessage.set(
            describeBackendError(
              err,
              'Não foi possível carregar as propostas de conta. Use Atualizar para tentar de novo.'
            )
          );
        },
      });
  }

  select(id: string): void {
    if (this.selectedId() === id) return;
    this.selectedId.set(id);
    this.reason.set('');
  }

  statusLabel(status: StandardAccountPromotionStatus): string {
    return STATUS_LABELS[status] ?? status;
  }

  formatShare(share: number | null): string {
    return share == null ? '—' : PERCENT_FORMAT.format(share);
  }

  decide(accept: boolean): void {
    const proposal = this.selected();
    if (!proposal || !this.canSubmitDecision()) return;
    const body = { reason: this.reason().trim() };
    const request: Observable<null> = accept
      ? this.governance.acceptPromotion(proposal.id, body)
      : this.governance.rejectPromotion(proposal.id, body);

    this.isDeciding.set(true);
    request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.isDeciding.set(false);
        this.reason.set('');
        this.toast.showToastSuccess(
          accept
            ? 'Proposta aceita: a conta oficial foi criada sob a mesma conta padrão e as linhas das empresas foram reclassificadas.'
            : 'Proposta recusada e registrada.'
        );
        this.load();
      },
      error: (err: unknown) => {
        this.isDeciding.set(false);
        this.toast.showToastError(
          describeBackendError(
            err,
            'Não foi possível registrar a decisão. Confira o motivo e tente de novo.'
          )
        );
      },
    });
  }

  /** Roda de novo a reclassificação das linhas para a conta oficial (retoma o que ficou pendente; repetir não duplica). */
  reclassify(): void {
    const proposal = this.selected();
    if (
      !proposal ||
      proposal.status !== 'Accepted' ||
      !this.canReclassify() ||
      this.isReclassifying()
    )
      return;

    this.isReclassifying.set(true);
    this.governance
      .reclassifyPromotion(proposal.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          this.isReclassifying.set(false);
          if (result?.completed) {
            this.toast.showToastSuccess(
              `Reclassificação concluída: ${result.movedLines} linha(s) movida(s) nesta rodada.`
            );
          } else {
            this.toast.showToastError(
              result?.failures?.[0] ??
                'A reclassificação ainda tem pendência. A rotina semanal tenta de novo; use Reclassificar de novo para tentar agora.'
            );
          }
          this.load();
        },
        error: (err: unknown) => {
          this.isReclassifying.set(false);
          this.toast.showToastError(
            describeBackendError(
              err,
              'Não foi possível reclassificar agora. A rotina semanal tenta de novo; use Reclassificar de novo para tentar agora.'
            )
          );
        },
      });
  }
}
